import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Boxes, Plus, Search, MoreHorizontal, Pencil, Trash2, ArrowUpCircle, ArrowDownCircle, AlertTriangle, Wrench, Package, ShoppingBag, Download, FileText, ArrowUp, ArrowDown, ArrowUpDown, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['servicio', 'material', 'producto', 'expense'];
const CATEGORY_ICONS = { servicio: Wrench, material: Package, producto: ShoppingBag, expense: Wallet };
const CATEGORY_COLORS = { servicio: 'bg-blue-50 text-blue-700', material: 'bg-amber-50 text-amber-700', producto: 'bg-emerald-50 text-emerald-700', expense: 'bg-rose-50 text-rose-700' };

export default function InventoryPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'servicio', sku: '', supplier_sku: '', unit: 'unidad', quantity: 0, min_quantity: 10, unit_cost: 0, selling_price: 0, inventory_tracked: false, supplier: '', location: '', notes: '', description: '' });
  const [adjustForm, setAdjustForm] = useState({ quantity: 0, type: 'add', reason: '', job_id: '' });
  // SKU sort (Alian's video 2026-02-12 — ↑↓ control to group by SKU)
  const [skuSort, setSkuSort] = useState(null); // null | 'asc' | 'desc'

  const CATEGORY_LABELS = {
    servicio: t('inventory.categoryServicio'),
    material: t('inventory.categoryMaterial'),
    producto: t('inventory.categoryProducto'),
    expense: t('inventory.categoryExpense'),
  };
  const getCategoryLabel = (cat) => CATEGORY_LABELS[cat] || (cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : '');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (search) params.search = search;
      if (showLowStock) params.low_stock = true;
      const [itemsRes, alertsRes] = await Promise.all([
        api.get('/inventory', { params }),
        api.get('/inventory/alerts')
      ]);
      setItems(itemsRes.data);
      setAlerts(alertsRes.data);
    } catch { toast.error(t('inventory.errorLoading')); }
    finally { setLoading(false); }
  }, [categoryFilter, search, showLowStock, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', category: 'servicio', sku: '', supplier_sku: '', unit: 'unidad', quantity: 0, min_quantity: 10, unit_cost: 0, selling_price: 0, inventory_tracked: false, supplier: '', location: '', notes: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, category: item.category || 'servicio', sku: item.sku || '', supplier_sku: item.supplier_sku || '', unit: item.unit || 'unidad', quantity: item.quantity || 0, min_quantity: item.min_quantity || 10, unit_cost: item.unit_cost || 0, selling_price: item.selling_price || 0, inventory_tracked: item.inventory_tracked || false, supplier: item.supplier || '', location: item.location || '', notes: item.notes || '', description: item.description || '' });
    setDialogOpen(true);
  };

  const openAdjust = (item, type) => {
    setSelectedItem(item);
    setAdjustForm({ quantity: 0, type, reason: '', job_id: '' });
    setAdjustDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('inventory.nameRequired')); return; }
    try {
      if (editing) {
        await api.put(`/inventory/${editing.id}`, form);
        toast.success(t('inventory.updated'));
      } else {
        await api.post('/inventory', form);
        toast.success(t('inventory.created'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('inventory.errorSaving')); }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!selectedItem || adjustForm.quantity <= 0) { toast.error(t('inventory.invalidQuantity')); return; }
    try {
      await api.post(`/inventory/${selectedItem.id}/adjust`, adjustForm);
      toast.success(adjustForm.type === 'add' ? t('inventory.stockAdded') : t('inventory.stockRemoved'));
      setAdjustDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('inventory.adjustError')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/inventory/${id}`); toast.success(t('inventory.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('bw_token');
      const params = categoryFilter && categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
      const res = await fetch(`${API_URL}/api/inventory/export/csv${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `inventory_${new Date().toISOString().split('T')[0]}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export error'); }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('bw_token');
      const params = categoryFilter && categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
      const res = await fetch(`${API_URL}/api/inventory/export/pdf${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `inventory_${new Date().toISOString().split('T')[0]}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('Export error'); }
  };

  const totalValue = items.reduce((s, i) => s + (i.total_value || 0), 0);

  // Apply SKU sort if active (Alian's video 2026-02-12)
  const sortedItems = (() => {
    if (!skuSort) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      const sa = (a.sku || '').toString();
      const sb = (b.sku || '').toString();
      return skuSort === 'asc' ? sa.localeCompare(sb, undefined, { numeric: true }) : sb.localeCompare(sa, undefined, { numeric: true });
    });
    return copy;
  })();

  const toggleSkuSort = () => {
    setSkuSort(prev => prev === null ? 'asc' : prev === 'asc' ? 'desc' : null);
  };

  return (
    <div data-testid="inventory-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Boxes className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('inventory.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('inventory.totalItems', { count: items.length })} | {t('inventory.totalValue', { value: totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 }) })}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-inventory-btn" variant="outline" size="sm" className="shadow-sm">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="export-csv-btn" onClick={handleExportCSV}>
                <FileText className="w-3.5 h-3.5 mr-2" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-pdf-btn" onClick={handleExportPDF}>
                <FileText className="w-3.5 h-3.5 mr-2 text-red-500" /> Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="new-inventory-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> {t('inventory.newItem')}
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{t('inventory.lowStockAlert', { count: alerts.length })}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {alerts.slice(0, 5).map(a => (
                <span key={a.id} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                  {a.name}: {a.quantity} {a.unit}
                </span>
              ))}
              {alerts.length > 5 && <span className="text-xs text-amber-600">+{alerts.length - 5}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-inventory" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('inventory.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="category-filter" className="w-48 h-9 bg-white">
            <SelectValue placeholder={t('inventory.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          data-testid="low-stock-filter"
          variant={showLowStock ? 'default' : 'outline'}
          onClick={() => setShowLowStock(!showLowStock)}
          className={showLowStock ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
          size="sm"
        >
          <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {t('inventory.lowStock')}
        </Button>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <Boxes className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('inventory.noItems')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('inventory.createFirstItem')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('lineItems.name')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">
                      <button
                        type="button"
                        onClick={toggleSkuSort}
                        data-testid="sku-sort-toggle"
                        className="inline-flex items-center gap-1 hover:text-[#003366] transition-colors"
                        title={t('inventory.sortBySku')}
                      >
                        {t('inventory.sku')}
                        {skuSort === 'asc' ? <ArrowUp className="w-3 h-3" /> : skuSort === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                      </button>
                    </th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('inventory.category')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('inventory.quantity')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('inventory.costPrice')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('common.value')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => {
                    const isLow = item.quantity <= item.min_quantity;
                    return (
                      <tr key={item.id} data-testid={`inventory-row-${item.id}`} className={`border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors ${isLow ? 'bg-amber-50/30' : ''}`}>
                        <td className="px-5 py-3.5">
                          <p className="text-sm font-medium text-gray-900">{item.name}</p>
                          {item.supplier && <p className="text-xs text-gray-400">{item.supplier}</p>}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <p className="text-sm font-mono text-[#003366] font-medium">{item.sku}</p>
                          {item.supplier_sku && <p className="text-xs font-mono text-gray-400">{item.supplier_sku}</p>}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600'}`}>{getCategoryLabel(item.category)}</span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className={`text-sm font-semibold ${isLow ? 'text-amber-600' : 'text-gray-700'}`}>
                            {item.quantity} {item.unit}
                          </span>
                          {isLow && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 inline ml-1" />}
                        </td>
                        <td className="px-5 py-3.5 text-right text-sm text-gray-600 hidden lg:table-cell">${(item.unit_cost || 0).toFixed(2)}</td>
                        <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700 hidden lg:table-cell">${(item.total_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" data-testid={`add-stock-${item.id}`} onClick={() => openAdjust(item, 'add')} className="h-8 w-8 text-emerald-500 hover:text-emerald-700">
                              <ArrowUpCircle className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" data-testid={`remove-stock-${item.id}`} onClick={() => openAdjust(item, 'remove')} className="h-8 w-8 text-red-400 hover:text-red-600">
                              <ArrowDownCircle className="w-4 h-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Boxes className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('inventory.noItems')}</p>
          </CardContent></Card>
        ) : (
          sortedItems.map((item, idx) => {
            const isLow = item.quantity <= item.min_quantity;
            return (
              <Card key={item.id} data-testid={`inventory-mobile-${item.id}`} className={`bg-white border-gray-100 shadow-sm mobile-card-enter ${isLow ? 'border-amber-200' : ''}`} style={{ animationDelay: `${idx * 50}ms` }}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${CATEGORY_COLORS[item.category] || 'bg-slate-100 text-slate-600'}`}>{getCategoryLabel(item.category)}</span>
                        {item.sku && <span className="text-xs text-gray-400 font-mono">{item.sku}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openAdjust(item, 'add')} className="h-8 w-8 text-emerald-500"><ArrowUpCircle className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openAdjust(item, 'remove')} className="h-8 w-8 text-red-400"><ArrowDownCircle className="w-4 h-4" /></Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-bold ${isLow ? 'text-amber-600' : 'text-gray-800'}`}>
                      {item.quantity} {item.unit} {isLow && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
                    </span>
                    <span className="text-xs text-gray-500">${(item.total_value || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('inventory.editItem') : t('inventory.newItem')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('inventory.itemName')} *</Label>
                <Input data-testid="inventory-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('inventory.namePlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.category')}</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, inventory_tracked: v === 'material' ? form.inventory_tracked : false })}>
                  <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>
                        <span className="flex items-center gap-2">
                          {CATEGORY_LABELS[c]}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.sku')}</Label>
                <Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder={t('inventory.autogenerated')} />
                <p className="text-xs text-gray-400">{t('inventory.internalSkuHint')}</p>
              </div>

              {/* Description - for Servicio and Producto */}
              {(form.category === 'servicio' || form.category === 'producto') && (
                <div className="sm:col-span-2 space-y-2">
                  <Label>{t('inventory.description')}</Label>
                  <Textarea data-testid="inventory-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={form.category === 'servicio' ? t('inventory.descriptionServicePlaceholder') : t('inventory.descriptionProductPlaceholder')} rows={2} />
                </div>
              )}

              {/* Supplier SKU - only for Material */}
              {form.category === 'material' && (
                <div className="space-y-2">
                  <Label>{t('inventory.supplierSku')}</Label>
                  <Input value={form.supplier_sku} onChange={e => setForm({ ...form, supplier_sku: e.target.value })} placeholder={t('inventory.supplierSkuPlaceholder')} />
                  <p className="text-xs text-gray-400">{t('inventory.supplierSkuHint')}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('lineItems.unit')}</Label>
                <Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder={form.category === 'servicio' ? t('inventory.unitServicePlaceholder') : t('inventory.unitPlaceholder')} />
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.unitCostLabel')}</Label>
                <Input type="number" value={form.unit_cost} onChange={e => setForm({ ...form, unit_cost: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
              </div>
              {form.category !== 'expense' && (
                <div className="space-y-2">
                  <Label>{t('inventory.sellingPriceLabel')}</Label>
                  <Input data-testid="selling-price-input" type="number" value={form.selling_price} onChange={e => setForm({ ...form, selling_price: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
                </div>
              )}

              {/* Inventory toggle - only for Material */}
              {form.category === 'material' && (
                <div className="sm:col-span-2 flex items-center justify-between p-3 rounded-lg bg-amber-50/50 border border-amber-100">
                  <div>
                    <Label className="text-sm font-medium">{t('inventory.trackInventory')}</Label>
                    <p className="text-xs text-gray-500 mt-0.5">{form.inventory_tracked ? t('inventory.trackedHelp') : t('inventory.notTrackedHelp')}</p>
                  </div>
                  <Switch data-testid="inventory-tracked-toggle" checked={form.inventory_tracked} onCheckedChange={v => setForm({ ...form, inventory_tracked: v })} />
                </div>
              )}

              {/* Stock fields - only for Material with inventory_tracked = true */}
              {form.category === 'material' && form.inventory_tracked && (
                <>
                  <div className="space-y-2">
                    <Label>{t('inventory.initialQuantity')}</Label>
                    <Input type="number" value={form.quantity} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} min="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('inventory.minQuantity')}</Label>
                    <Input type="number" value={form.min_quantity} onChange={e => setForm({ ...form, min_quantity: parseFloat(e.target.value) || 0 })} min="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('inventory.supplier')}</Label>
                    <Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} placeholder={t('inventory.supplierName')} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('inventory.location')}</Label>
                    <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder={t('inventory.warehouseLocation')} />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-inventory-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('inventory.saveChanges') : t('inventory.createItem')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              {adjustForm.type === 'add' ? <ArrowUpCircle className="w-5 h-5 text-emerald-500" /> : <ArrowDownCircle className="w-5 h-5 text-red-500" />}
              {adjustForm.type === 'add' ? t('inventory.addStock') : t('inventory.removeStock')}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <form onSubmit={handleAdjust} className="space-y-4">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                <p className="text-sm font-medium">{selectedItem.name}</p>
                <p className="text-xs text-gray-500">{t('inventory.currentStock', { quantity: selectedItem.quantity, unit: selectedItem.unit })}</p>
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.quantity')}</Label>
                <Input data-testid="adjust-quantity" type="number" value={adjustForm.quantity} onChange={e => setAdjustForm({ ...adjustForm, quantity: parseFloat(e.target.value) || 0 })} min="0" step="0.01" />
              </div>
              <div className="space-y-2">
                <Label>{t('inventory.reason')}</Label>
                <Input value={adjustForm.reason} onChange={e => setAdjustForm({ ...adjustForm, reason: e.target.value })} placeholder={t('inventory.reasonPlaceholder')} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button data-testid="confirm-adjust-btn" type="submit" className={adjustForm.type === 'add' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}>
                  {t('common.confirm')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
